/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useState, useEffect, useRef } from "react";
import Guacamole from "guacamole-common-js";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getAppState, changeAppState, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

const ErrorContent = (msg:string) => (
  <div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}>
    <h2 className="uk-margin-remove uk-text-center txt-white txt-stroke-red-shadow">{msg}</h2>
  </div>
);

const LoadingSpinner = (loader:string) => (<div className="uk-flex uk-flex-center uk-flex-middle" style={{height: "100%"}}><div className={loader}></div></div>);

const getGuacDisconnectTimers = ():Record<string, any> => {
  const ww:any = window;
  if (!ww.__guacDisconnectTimers) { ww.__guacDisconnectTimers = {} }
  return ww.__guacDisconnectTimers;
};

const getGuacActiveClients = ():Record<string, any> => {
  const ww:any = window;
  if (!ww.__guacActiveClients) { ww.__guacActiveClients = {} }
  return ww.__guacActiveClients;
};

type Tcomponent = {
  rowkey?:string,
  loader:string,
  viewOnly?:boolean
};

const GuacamoleViewer:FC<TuniversalElementProps> = ({attributes, children}) => {
  GuacamoleViewer.displayName = "GuacamoleViewer";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);

  const {rootAttributes, key, fullPath, type, content} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const containerRef = useRef(null);
  const clientRef = useRef(null);
  const runtimeContextRef = useRef<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [connected, setConnected] = useState<boolean>(false);
  const [host, setHost] = useState<{[key:string]:any} | null>(null);

  const cmpType = useMemo<Tcomponent>(() => {
    const typ:Tcomponent = {rowkey: "", loader: "loader02"};
    (type || "").replace((/(\s+|_|\|)/igm), " ").toLowerCase().split(" ").forEach((tt) => {
      if (~["viewonly", "view", "readonly", "read"].indexOf(tt)) { typ.viewOnly = true }
      else if (tt.startsWith("loader")) { typ.loader = tt }
      // else if (~["rdp", "vnc", "ssh"].indexOf(tt)) { typ.protocol = tt }
    });

    if (elState?.value) { typ.rowkey = elState?.value }
    else if (elState?.attributes?.host) { typ.rowkey = elState?.attributes.host }
    else if (elState?.attributes?.hostkey) { typ.rowkey = elState?.attributes.hostkey }
    else if (elState?.attributes?.rowkey) { typ.rowkey = elState?.attributes.rowkey }
    if (elState?.attributes?.viewOnly) { typ.viewOnly = Boolean(elState?.attributes.viewOnly) }
    if (elState?.attributes?.loader) { typ.loader = elState?.attributes.loader }
    return typ;
  }, [type, elState?.value, elState?.attributes.host, elState?.attributes.hostkey, elState?.attributes.rowkey, elState?.attributes.viewOnly, elState?.attributes.loader]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }

    if (!connected) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      if (!~userClss.indexOf("uk-flex-middle")) { userClss.push("uk-flex-middle") }
      if (!~userClss.indexOf("uk-flex-center")) { userClss.push("uk-flex-center") }
    }

    return userClss;
  }, [connected, elState?.disabled, elState?.userClass, permissions.disabled]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    if (!est.position) { est.position = "relative" }

    return est;
  }, [elState.userStyle, elState?.visible, permissions.visible]);

  useEffect(() => {
    runtimeContextRef.current = {
      rootNamespace,
      onConnectHandler: attributes.onConnect || attributes.onconnect,
      onDisconnectHandler: attributes.onDisconnect || attributes.ondisconnect,
      rootAppKey: rootAttributes.key,
      attributes, key, hostRowkey: cmpType.rowkey, user, lng, dct, safe,
      dispatch
    };
  }, [attributes, cmpType.rowkey, dct, dispatch, key, lng, rootAttributes.key, rootNamespace, safe, user]);

  useEffect(() => {
    if (!cmpType.rowkey) { setErrorMessage("Attribute 'host' is undefined"); return }
    const controller = new AbortController();
    setErrorMessage("");
    setConnected(false);
    setHost(null);
    fetch("/api/guacconnections", {signal: controller.signal}).then(async (res1) => {
      if (!res1 || res1.status !== 200) { setErrorMessage("Error getting data from server"); return }
      const ans1 = await res1.json();
      if (!ans1 || ans1.err) { setErrorMessage(ans1.err || "Error"); return }

      const iCon = (ans1.connections || []).find((cc) => (cc.rowkey === cmpType.rowkey));
      if (!iCon) { setErrorMessage("Connection '" + cmpType.rowkey + "' not found in program settings"); return }
      if (!rz.arrCompare(iCon.allowedGroups, user.groups || [], "includesAny") && !rz.arrContains(iCon.allowedUsers, user.login)) {
        setErrorMessage("You do not have permission to view this connection");
        return;
      }
      setHost(iCon);
    }).catch((err) => {
      if (err?.name === "AbortError") { return }
      console.error("Error fetching Guacamole connections:", err);
      setErrorMessage("Error getting data from server: " + rz.errToString(err));
    });
    return () => {
      controller.abort();
    };
  // Important: do not depend on user/lng here, otherwise Redux updates can trigger
  // token reload -> host reset -> connection cleanup/disconnect right after OPEN.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmpType.rowkey]);

  useEffect(() => {
    const timerId = `${rootAttributes.key || ""}:${key || ""}:${cmpType.rowkey || ""}`;
    const timers = getGuacDisconnectTimers();
    if (timers[timerId]) {
      clearTimeout(timers[timerId]);
      delete timers[timerId];
    }
  }, [cmpType.rowkey, key, rootAttributes.key]);

  useEffect(() => {
    if (!host || !host.token || !cmpType.rowkey || !containerRef?.current) { return }

    const timerId = `${rootAttributes.key || ""}:${key || ""}:${cmpType.rowkey || ""}`;
    const timers = getGuacDisconnectTimers();
    if (timers[timerId]) {
      clearTimeout(timers[timerId]);
      delete timers[timerId];
    }

    try {
      const webSocketProtocol = (window.location.protocol === "https:" ? "wss" : "ws");
      const tunnel = new Guacamole.WebSocketTunnel(`${webSocketProtocol}://${window.location.host}/guacamole`);
      const client = new Guacamole.Client(tunnel);
      const activeClients = getGuacActiveClients();
      const prevClient = activeClients[timerId];
      if (prevClient && prevClient !== client) {
        try { prevClient.disconnect() } catch (err) { console.error(err) }
      }
      activeClients[timerId] = client;
      clientRef.current = client;
      const connectData = `rowkey=${encodeURIComponent(cmpType.rowkey)}&token=${encodeURIComponent(host.token)}`;

      rz.log({message: "Connecting to Guacamole server with rowkey '" + cmpType.rowkey + "'...", level: "info", module: "GuacamoleViewer.tsx", caller: "useEffect(connect)"});

      // display
      const displayEl = client.getDisplay().getElement();
      displayEl.tabIndex = 0;
      displayEl.style.outline = "none";
      const scaleRef = {current: 1};
      const fitInProgressRef = {current: false};
      const remoteClipboardTextRef = {current: ""};
      // @ts-ignore
      containerRef.current.innerHTML = ""; containerRef.current.appendChild(displayEl);

      const fitDisplay = () => {
        if (fitInProgressRef.current) { return }
        fitInProgressRef.current = true;
        try {
          if (!containerRef.current) { return }
          // @ts-ignore
          const width = Number(containerRef.current.clientWidth || 0);
          // @ts-ignore
          const height = Number(containerRef.current.clientHeight || 0);
          if (width < 16 || height < 16) { return }

          client.sendSize(width, height);

          const gDisplay = client.getDisplay();
          const remoteWidth = Number(gDisplay.getWidth() || 0);
          const remoteHeight = Number(gDisplay.getHeight() || 0);
          if (remoteWidth > 0 && remoteHeight > 0) {
            const scale = Math.min(width / remoteWidth, height / remoteHeight);
            if (Number.isFinite(scale) && scale > 0) {
              if (Math.abs(scale - scaleRef.current) > 0.001) {
                scaleRef.current = scale;
                gDisplay.scale(scale);
              }
              displayEl.style.marginLeft = "0px";
              displayEl.style.marginTop = "0px";
            }
          }
        } finally {
          fitInProgressRef.current = false;
        }
      };

      client.getDisplay().onresize = () => { setTimeout(() => { fitDisplay() }, 0) };

      let resizeObserver:any = null;
      if (typeof window !== "undefined" && (window as any).ResizeObserver && containerRef.current) {
        resizeObserver = new (window as any).ResizeObserver(() => { fitDisplay() });
        resizeObserver.observe(containerRef.current);
      }

      // events
      tunnel.onstatechange = (state) => {
        if (state === Guacamole.Tunnel.State.OPEN) {
          setConnected(true);
          setErrorMessage("");
          setTimeout(() => { fitDisplay() }, 0);

          const ctx = runtimeContextRef.current || {};
          if (fnc.config?.isDev) console.log({action: "<Guacamole key='" + (ctx.key || key) + "' host='" + (ctx.hostRowkey || cmpType.rowkey) + "'> -> onConnect()"});
          if (ctx.rootNamespace && ctx.onConnectHandler && ctx.rootNamespace[ctx.onConnectHandler]) {
            const clientFNC = ctx.rootNamespace[ctx.onConnectHandler];
            if (rz.isPromise(clientFNC)) {
              clientFNC({appkey: ctx.rootAppKey, attributes: ctx.attributes, key: ctx.key, host: ctx.hostRowkey, user: ctx.user, lng: ctx.lng, dct: ctx.dct, safe: ctx.safe}).then((ret) => {
                if (rz.isUndefinedOrNull(ret)) { return }
                if (ret?.err) { console.log(ret.err) }
                if (ret && ret?.state?.fullPath && ret?.state?.newState) {
                  ctx.dispatch(changeAppState({appkey: ctx.rootAppKey, fullPath: ret.state.fullPath, newState: ret.state.newState}));
                }
              });
            }
            else if (rz.isFunction(clientFNC)) {
              const ret = clientFNC({appkey: ctx.rootAppKey, attributes: ctx.attributes, key: ctx.key, host: ctx.hostRowkey, user: ctx.user, lng: ctx.lng, dct: ctx.dct, safe: ctx.safe}); if (rz.isUndefinedOrNull(ret)) { return }
              if (ret?.err) { console.log(ret.err) }
              if (ret && ret?.state?.fullPath && ret?.state?.newState) {
                ctx.dispatch(changeAppState({appkey: ctx.rootAppKey, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          }
        }
        else if (state === Guacamole.Tunnel.State.CLOSED) {
          setConnected(false);
          setErrorMessage("Connection closed");

          const ctx = runtimeContextRef.current || {};
          if (fnc.config?.isDev) console.log({action: "<Guacamole key='" + (ctx.key || key) + "' host='" + (ctx.hostRowkey || cmpType.rowkey) + "'> -> onDisconnect()"});
          if (ctx.rootNamespace && ctx.onDisconnectHandler && ctx.rootNamespace[ctx.onDisconnectHandler]) {
            const clientFNC = ctx.rootNamespace[ctx.onDisconnectHandler];
            if (rz.isPromise(clientFNC)) {
              clientFNC({appkey: ctx.rootAppKey, attributes: ctx.attributes, key: ctx.key, host: ctx.hostRowkey, user: ctx.user, lng: ctx.lng, dct: ctx.dct, safe: ctx.safe}).then((ret) => {
                if (rz.isUndefinedOrNull(ret)) { return }
                if (ret?.err) { console.log(ret.err) }
                if (ret && ret?.state?.fullPath && ret?.state?.newState) {
                  ctx.dispatch(changeAppState({appkey: ctx.rootAppKey, fullPath: ret.state.fullPath, newState: ret.state.newState}));
                }
              });
            }
            else if (rz.isFunction(clientFNC)) {
              const ret = clientFNC({appkey: ctx.rootAppKey, attributes: ctx.attributes, key: ctx.key, host: ctx.hostRowkey, user: ctx.user, lng: ctx.lng, dct: ctx.dct, safe: ctx.safe}); if (rz.isUndefinedOrNull(ret)) { return }
              if (ret?.err) { console.log(ret.err) }
              if (ret && ret?.state?.fullPath && ret?.state?.newState) {
                ctx.dispatch(changeAppState({appkey: ctx.rootAppKey, fullPath: ret.state.fullPath, newState: ret.state.newState}));
              }
            }
          }
        }
      };

      tunnel.onerror = (err) => {
        if (err?.message === "Connection closed normally") { return }
        console.error("Guacamole tunnel error:", err);
        setErrorMessage("Connection failed: " + rz.errToString(err));
      };

      // connect
      client.connect(connectData);

      // mouse
      const mouse = new Guacamole.Mouse(displayEl);
      const clamp = (value:number, min:number, max:number) => (Math.max(min, Math.min(max, value)));
      const mapPointerState = (state:any) => {
        const mappedState = {...state};
        const gDisplay = client.getDisplay();
        const remoteWidth = Number(gDisplay.getWidth() || 0);
        const remoteHeight = Number(gDisplay.getHeight() || 0);
        const rect = displayEl.getBoundingClientRect();

        if (remoteWidth > 0 && remoteHeight > 0 && rect.width > 0 && rect.height > 0) {
          const mappedX = Math.round((Number(mappedState.x || 0) / rect.width) * remoteWidth);
          const mappedY = Math.round((Number(mappedState.y || 0) / rect.height) * remoteHeight);
          mappedState.x = clamp(mappedX, 0, Math.max(0, remoteWidth - 1));
          mappedState.y = clamp(mappedY, 0, Math.max(0, remoteHeight - 1));
          return mappedState;
        }

        const scale = Number(scaleRef.current || 1);
        if (Number.isFinite(scale) && scale > 0 && scale !== 1) {
          mappedState.x = Math.round(Number(mappedState.x || 0) / scale);
          mappedState.y = Math.round(Number(mappedState.y || 0) / scale);
        }
        return mappedState;
      };
      const sendMouseState = (state) => {
        client.sendMouseState(mapPointerState(state))
      };
      const focusDisplay = () => {
        try { displayEl.focus() } catch (err) { console.error(err) }
      };
      const viewerFocusedRef = {current: false};
      const isViewerEvent = (evt:any) => {
        const target = evt?.target;
        if (!target) { return false }
        return (target === displayEl || displayEl.contains(target));
      };
      const isViewerActive = () => {
        if (viewerFocusedRef.current) { return true }
        const activeEl:any = document.activeElement;
        return Boolean(activeEl && displayEl.contains(activeEl));
      };
      const handleDisplayFocus = () => {
        viewerFocusedRef.current = true;
      };
      const handleDisplayBlur = (evt:any) => {
        const nextTarget = evt?.relatedTarget;
        viewerFocusedRef.current = Boolean(nextTarget && displayEl.contains(nextTarget));
      };
      const handleDocumentPointerDown = (evt:any) => {
        viewerFocusedRef.current = isViewerEvent(evt);
      };

      displayEl.addEventListener("mousedown", focusDisplay);
      displayEl.addEventListener("focus", handleDisplayFocus);
      displayEl.addEventListener("blur", handleDisplayBlur);
      document.addEventListener("pointerdown", handleDocumentPointerDown, true);
      mouse.onmousedown = (state) => { sendMouseState(state) }
      mouse.onmouseup = (state) => { sendMouseState(state) }
      mouse.onmousemove = (state) => { sendMouseState(state) }

      // keyboard
      const keyboard = new Guacamole.Keyboard(document);
      keyboard.onkeydown = (keysym) => {
        if (!isViewerActive()) { return true }
        client.sendKeyEvent(1, keysym);
        return false;
      }
      keyboard.onkeyup = (keysym) => {
        if (!isViewerActive()) { return true }
        client.sendKeyEvent(0, keysym);
        return false;
      }

      const handleCtrlLayoutShortcut = (evt:any) => {
        try {
          if (!isViewerEvent(evt) && !isViewerActive()) { return }

          const isCtrlCombo = (evt?.ctrlKey || evt?.metaKey) && !evt?.altKey;
          if (!isCtrlCombo) { return }

          const code = String(evt?.code || "");
          const keyMatch = code.match(/^Key([A-Z])$/);
          if (!keyMatch) { return }
          const latinChar = String(keyMatch[1] || "").toLowerCase();
          if (!latinChar) { return }

          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation?.();

          const latinKeysym = latinChar.charCodeAt(0);
          client.sendKeyEvent(1, latinKeysym);
          client.sendKeyEvent(0, latinKeysym);

          if (code === "KeyV") {
            const nav:any = navigator;
            if (nav?.clipboard?.readText) {
              nav.clipboard.readText().then((clipboardText:string) => {
                if (clipboardText) {
                  try {
                    const clipboardStream = client.createClipboardStream("text/plain");
                    const stringWriter = new (Guacamole as any).StringWriter(clipboardStream);
                    stringWriter.sendText(String(clipboardText));
                    stringWriter.sendEnd();
                  } catch (err) {
                    console.error("Failed to send clipboard on Ctrl+V:", err);
                  }
                }
              }).catch((err:any) => {
                console.error("Clipboard sync on Ctrl+V failed:", err);
              });
            }
          }
        } catch (err) {
          console.error("Ctrl layout shortcut bridge error:", err);
        }
      };

      const writeTextToRemoteClipboard = (text:string) => {
        if (!rz.isString(text)) { return }
        const clipboardText = String(text || "");
        try {
          const clipboardStream = client.createClipboardStream("text/plain");
          const stringWriter = new (Guacamole as any).StringWriter(clipboardStream);
          stringWriter.sendText(clipboardText);
          stringWriter.sendEnd();
        } catch (err) {
          console.error("Failed to send clipboard to remote session:", err);
        }
      };

      const writeTextToLocalClipboard = async (text:string) => {
        const clipboardText = String(text || "");
        if (!clipboardText) { return }
        const nav:any = navigator;

        try {
          if (nav?.clipboard?.writeText) {
            await nav.clipboard.writeText(clipboardText);
            return;
          }
        } catch {
          // fallback below
        }

        try {
          const el = document.createElement("textarea");
          el.value = clipboardText;
          el.setAttribute("readonly", "readonly");
          el.style.position = "fixed";
          el.style.left = "-9999px";
          el.style.opacity = "0";
          document.body.appendChild(el);
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
        } catch (err) {
          console.error("Failed to copy remote clipboard to local clipboard:", err);
        }
      };

      const syncClipboardToRemote = async (preferredText?:string) => {
        if (rz.isString(preferredText) && String(preferredText).length > 0) {
          writeTextToRemoteClipboard(String(preferredText));
          return;
        }

        try {
          const nav:any = navigator;
          if (nav?.clipboard?.readText) {
            const clipboardText = await nav.clipboard.readText();
            if (clipboardText) {
              writeTextToRemoteClipboard(clipboardText);
            }
          }
        } catch (err) {
          console.error("Clipboard readText() failed:", err);
        }
      };

      const handlePaste = async (evt:any) => {
        try {
          if (!isViewerEvent(evt) && !isViewerActive()) { return }

          evt?.preventDefault?.();
          evt?.stopPropagation?.();
          evt?.stopImmediatePropagation?.();

          const eventText = evt?.clipboardData?.getData?.("text/plain") || evt?.clipboardData?.getData?.("text") || "";
          await syncClipboardToRemote(eventText);
        } catch (err) {
          console.error("Clipboard paste bridge error:", err);
        }
      };

      const handlePasteShortcut = async (evt:any) => {
        try {
          if (!isViewerEvent(evt) && !isViewerActive()) { return }

          const code = String(evt?.code || "");
          const isPasteShortcut = (evt?.ctrlKey || evt?.metaKey) && !evt?.altKey && code === "KeyV";
          if (!isPasteShortcut) { return }

          // Do not block native/guacamole key handling here.
          // We only pre-sync local clipboard to remote clipboard buffer.
          await syncClipboardToRemote();
        } catch (err) {
          console.error("Clipboard shortcut bridge error:", err);
        }
      };

      const handleContextMenu = () => {
        if (!isViewerActive()) { return }
        syncClipboardToRemote().catch((err) => {
          console.error("Clipboard contextmenu sync error:", err);
        });
      };

      const handleCopyOrCut = (evt:any) => {
        const remoteText = String(remoteClipboardTextRef.current || "");
        if (!remoteText) { return }
        if (!evt?.clipboardData?.setData) { return }
        evt.preventDefault();
        evt.clipboardData.setData("text/plain", remoteText);
      };

      client.onclipboard = (stream:any, mimetype:string) => {
        if (!stream) { return }
        if (mimetype && mimetype !== "text/plain") {
          stream.sendAck("Only text/plain clipboard is supported", 0x0100);
          return;
        }

        const reader = new (Guacamole as any).StringReader(stream);
        let incomingText = "";
        reader.ontext = (chunk:string) => {
          incomingText += String(chunk || "");
        };
        reader.onend = () => {
          remoteClipboardTextRef.current = incomingText;
          writeTextToLocalClipboard(incomingText).catch((err) => {
            console.error("Clipboard receive bridge error:", err);
          });
        };
      };

      displayEl.addEventListener("paste", handlePaste);
      displayEl.addEventListener("copy", handleCopyOrCut);
      displayEl.addEventListener("cut", handleCopyOrCut);
      displayEl.addEventListener("keydown", handlePasteShortcut, true);
      displayEl.addEventListener("keydown", handleCtrlLayoutShortcut, true);
      displayEl.addEventListener("contextmenu", handleContextMenu);
      document.addEventListener("paste", handlePaste, true);
      document.addEventListener("keydown", handlePasteShortcut, true);
      document.addEventListener("keydown", handleCtrlLayoutShortcut, true);
      window.addEventListener("paste", handlePaste, true);
      window.addEventListener("keydown", handlePasteShortcut, true);
      window.addEventListener("keydown", handleCtrlLayoutShortcut, true);

      // touch
      let touchscreen:any = null;
      if ((Guacamole as any).Touchscreen) {
        touchscreen = new (Guacamole as any).Touchscreen(displayEl);
        touchscreen.ontouchstart = (state) => { focusDisplay(); sendMouseState(state) };
        touchscreen.ontouchmove = (state) => { sendMouseState(state) };
        touchscreen.ontouchend = (state) => { sendMouseState(state) };
      }

      // resize
      const resize = () => { fitDisplay() };

      window.addEventListener("resize", resize);
      setTimeout(resize, 100);
      setTimeout(resize, 500);
      setTimeout(resize, 1200);

      // cleanup
      return () => {
        window.removeEventListener("resize", resize);
        displayEl.removeEventListener("mousedown", focusDisplay);
        displayEl.removeEventListener("focus", handleDisplayFocus);
        displayEl.removeEventListener("blur", handleDisplayBlur);
        document.removeEventListener("pointerdown", handleDocumentPointerDown, true);
        displayEl.removeEventListener("paste", handlePaste);
        displayEl.removeEventListener("copy", handleCopyOrCut);
        displayEl.removeEventListener("cut", handleCopyOrCut);
        displayEl.removeEventListener("keydown", handlePasteShortcut, true);
        displayEl.removeEventListener("keydown", handleCtrlLayoutShortcut, true);
        displayEl.removeEventListener("contextmenu", handleContextMenu);
        document.removeEventListener("paste", handlePaste, true);
        document.removeEventListener("keydown", handlePasteShortcut, true);
        document.removeEventListener("keydown", handleCtrlLayoutShortcut, true);
        window.removeEventListener("paste", handlePaste, true);
        window.removeEventListener("keydown", handlePasteShortcut, true);
        window.removeEventListener("keydown", handleCtrlLayoutShortcut, true);
        keyboard.onkeydown = null;
        keyboard.onkeyup = null;
        try { keyboard.reset() } catch (err) { console.error(err) }
        client.onclipboard = null;
        if (touchscreen) {
          touchscreen.ontouchstart = null;
          touchscreen.ontouchmove = null;
          touchscreen.ontouchend = null;
        }
        if (resizeObserver) { resizeObserver.disconnect() }
        client.getDisplay().onresize = null;

        const timers2 = getGuacDisconnectTimers();
        timers2[timerId] = setTimeout(() => {
          const activeClients2 = getGuacActiveClients();
          if (activeClients2[timerId] === client) { delete activeClients2[timerId] }
          if (clientRef.current === client) {
            try { client.disconnect() } catch (err) { console.error(err) }
            clientRef.current = null;
          }
          if (timers2[timerId]) { delete timers2[timerId] }
        }, 600);
      };
    } catch (err) {
      console.error(err);
      setErrorMessage("Guacamole initialization failed: " + rz.errToString(err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmpType.rowkey, host?.token]);

  return (
    <div key={key} id={key} style={styl}>
      <div key={key + "-container"} ref={containerRef} className={clsu.join(" ")} style={{width: "100%", height: "100%", background: "#000", overflow: "hidden"}} />
      <div key={key + "-loader"} style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: ((!connected && !errorMessage) ? "block" : "none")}}>
         {LoadingSpinner(cmpType.loader)}
      </div>
      <div key={key + "-error"} style={{position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: (errorMessage ? "block" : "none")}}>
        {ErrorContent(errorMessage || "")}
      </div>
    </div>
  );
}

export default GuacamoleViewer;
/* eslint-enable @typescript-eslint/no-unused-vars */
